import {
  AccessTime,
  VolunteerActivismOutlined,
  CheckCircleOutline,
  PlayCircleOutline,
  Diversity3Outlined,
  SpeedOutlined,
} from "@mui/icons-material";
import { Stack, Typography } from "@mui/material";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { useState, type FC } from "react";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { KPI_POPUP_VARIANT, type KpiKey } from "../kpiMeta";
import {
  buildStatisticsQueryFilters,
  EMPTY_FILTERS,
  type StatisticsFilters,
} from "../types";
import { KpiAvgTimeDistributionDialog } from "./KpiAvgTimeDistributionDialog";
import { KpiBreakdownDialog } from "./KpiBreakdownDialog";
import { KpiCard } from "./KpiCard";
import { MatchEfficiencyDialog } from "./MatchEfficiencyDialog";
import { StatisticsSegmentFilters } from "./StatisticsSegmentFilters";

export const KpiSection: FC = () => {
  const { currOrganization } = useOrganization();
  const [kpiFilters, setKpiFilters] =
    useState<StatisticsFilters>(EMPTY_FILTERS);
  const [openKpi, setOpenKpi] = useState<KpiKey | null>(null);
  const [matchEfficiencyOpen, setMatchEfficiencyOpen] = useState(false);
  const popupVariant = openKpi ? KPI_POPUP_VARIANT[openKpi] : null;

  type RealtimeKpi = {
    fullOpportunities: number;
    activeOpportunities: number;
    requiredVolunteers: number;
    unresolvedAidRequests: number;
    avgTimeToFullHours: number | null;
    matchRatePercent: number | null;
    matchedMissionsCount: number;
    matchableMissionsCount: number;
    matchedWithin24hPercent: number | null;
    medianMatchTimeHours: number | null;
  };

  const { data: kpi, isLoading } = useQuery<RealtimeKpi>({
    queryKey: ["statistics-kpi", currOrganization?.id, kpiFilters],
    queryFn: async () => {
      const { data } = await linko.statistics.queries.getKpi(
        buildStatisticsQueryFilters(currOrganization!.id, kpiFilters),
      );
      return data as unknown as RealtimeKpi;
    },
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  const formatHours = (hours: number | null | undefined) => {
    if (!hours) return "-";
    if (hours < 1) return `${Math.round(hours * 60)} דק׳`;
    if (hours < 24) return `${hours.toFixed(1)} שע׳`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return remainingHours > 0 ? `${days} ימים ${remainingHours} שע׳` : `${days} ימים`;
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value == null) return "—";
    return `${value}%`;
  };

  return (
    <>
      <Stack sx={{ width: "100%", gap: "1rem" }}>
        <Typography
          sx={{
            fontSize: "1.1rem",
            fontWeight: 600,
            color: "#05204c",
          }}
        >
          נתוני זמן אמת
        </Typography>

        <StatisticsSegmentFilters filters={kpiFilters} onChange={setKpiFilters} />

        <Stack
          direction="row"
          flexWrap="wrap"
          gap={2}
          justifyContent="flex-start"
        >
          <KpiCard
            label="משימות מלאות"
            value={kpi?.fullOpportunities ?? 0}
            icon={<CheckCircleOutline fontSize="inherit" />}
            color="#29bd29"
            isLoading={isLoading}
            onClick={() => setOpenKpi("fullOpportunities")}
          />
          <KpiCard
            label="משימות פתוחות"
            value={kpi?.activeOpportunities ?? 0}
            icon={<PlayCircleOutline fontSize="inherit" />}
            color="#065bf9"
            isLoading={isLoading}
            onClick={() => setOpenKpi("activeOpportunities")}
          />
          <KpiCard
            label="כמות מתנדבים דרושה"
            value={kpi?.requiredVolunteers ?? 0}
            icon={<Diversity3Outlined fontSize="inherit" />}
            color="#05204c"
            isLoading={isLoading}
            onClick={() => setOpenKpi("activeOpportunities")}
          />
          <KpiCard
            label="בקשות סיוע פתוחות"
            value={kpi?.unresolvedAidRequests ?? 0}
            icon={<VolunteerActivismOutlined fontSize="inherit" />}
            color="#e90d0d"
            isLoading={isLoading}
            onClick={() => setOpenKpi("unresolvedAidRequests")}
          />
          <KpiCard
            label="יעילות מאצ׳"
            value={formatPercent(kpi?.matchRatePercent)}
            icon={<SpeedOutlined fontSize="inherit" />}
            color="#00b4d8"
            isLoading={isLoading}
            onClick={() => setMatchEfficiencyOpen(true)}
          />
          <KpiCard
            label="זמן מאצ׳"
            value={formatHours(kpi?.avgTimeToFullHours)}
            icon={<AccessTime fontSize="inherit" />}
            color="#05204c"
            isLoading={isLoading}
            onClick={() => setOpenKpi("avgTimeToFull")}
          />
        </Stack>
      </Stack>

      <KpiBreakdownDialog
        open={openKpi !== null && popupVariant === "breakdown"}
        onClose={() => setOpenKpi(null)}
        kpiKey={openKpi}
        organizationId={currOrganization?.id}
        filters={kpiFilters}
      />
      <KpiAvgTimeDistributionDialog
        open={openKpi !== null && popupVariant === "avgTimeDistribution"}
        onClose={() => setOpenKpi(null)}
        organizationId={currOrganization?.id}
        filters={kpiFilters}
      />
      <MatchEfficiencyDialog
        open={matchEfficiencyOpen}
        onClose={() => setMatchEfficiencyOpen(false)}
        organizationId={currOrganization?.id}
        filters={kpiFilters}
        stats={kpi}
      />
    </>
  );
};
